import { useRef, useState } from "react";
import { Images, X, Check, WarningCircle } from "@phosphor-icons/react";

/**
 * BatchPhotoImport — pick 60–70 photos at once from the phone gallery
 * (Google Photos syncs there), upload them in a background queue with
 * per-photo progress. `upload(file, meta)` is supplied by the caller.
 */
export default function BatchPhotoImport({ title = "Import photos", subtitle, upload, onDone, onClose, dark = true, extra }) {
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);     // {id, url, name, status, takenAt}
  const [running, setRunning] = useState(false);
  const [concurrency] = useState(3);

  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    const add = files.map(f => ({ id: `${f.name}-${f.size}-${f.lastModified}`, file: f, name: f.name, url: URL.createObjectURL(f),
      status: "wait", takenAt: f.lastModified ? new Date(f.lastModified).toISOString() : null }));
    setItems(s => { const seen = new Set(s.map(x => x.id)); return [...s, ...add.filter(a => !seen.has(a.id))]; });
    e.target.value = "";
  };

  const start = async () => {
    setRunning(true);
    const queue = items.filter(i => i.status === "wait" || i.status === "err");
    let idx = 0;
    const worker = async () => {
      while (idx < queue.length) {
        const it = queue[idx++];
        setItems(s => s.map(x => x.id === it.id ? { ...x, status: "up" } : x));
        try { await upload(it.file, { takenAt: it.takenAt }); setItems(s => s.map(x => x.id === it.id ? { ...x, status: "ok" } : x)); }
        catch { setItems(s => s.map(x => x.id === it.id ? { ...x, status: "err" } : x)); }
      }
    };
    await Promise.all(Array.from({ length: concurrency }, worker));
    setRunning(false);
  };

  const ok = items.filter(i => i.status === "ok").length;
  const err = items.filter(i => i.status === "err").length;
  const allDone = items.length > 0 && ok + err === items.length;
  const bg = dark ? "bg-[#0b1c2d] text-white" : "bg-white text-[#0b1c2d]";

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${bg}`} data-testid="batch-import">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
        <Images weight="fill" size={26} className="text-teal-400"/>
        <div className="flex-1 min-w-0">
          <div className="font-black text-lg leading-tight">{title}</div>
          {subtitle && <div className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>{subtitle}</div>}
        </div>
        <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-testid="batch-close"><X size={20}/></button>
      </div>

      {extra}

      <div className="flex-1 overflow-y-auto p-3">
        {items.length === 0 ? (
          <button onClick={() => fileRef.current?.click()} data-testid="batch-pick"
            className="w-full h-56 rounded-3xl border-2 border-dashed border-teal-400/50 flex flex-col items-center justify-center gap-2 text-teal-300">
            <Images size={48}/>
            <div className="font-black text-lg">Select photos from gallery</div>
            <div className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>Long-press to multi-select · 60–70 at once is fine</div>
          </button>
        ) : (
          <div className="grid grid-cols-4 gap-1.5" data-testid="batch-grid">
            {items.map(it => (
              <div key={it.id} className="relative aspect-square rounded-lg overflow-hidden bg-black/30">
                <img src={it.url} alt="" className="w-full h-full object-cover" loading="lazy"/>
                {it.status === "up" && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><div className="w-5 h-5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"/></div>}
                {it.status === "ok" && <span className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"><Check weight="bold" size={12}/></span>}
                {it.status === "err" && <span className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-red-600 flex items-center justify-center"><WarningCircle weight="fill" size={12}/></span>}
                {it.status === "wait" && !running && (
                  <button onClick={() => setItems(s => s.filter(x => x.id !== it.id))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center"><X weight="bold" size={12}/></button>
                )}
              </div>
            ))}
            <button onClick={() => fileRef.current?.click()} disabled={running} className="aspect-square rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center text-slate-400" data-testid="batch-pick-more"><Images size={24}/></button>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-white/10 space-y-2">
        <div className="text-sm flex justify-between" data-testid="batch-status">
          <span>{items.length} selected</span>
          <span className="text-emerald-400 font-bold">{ok} uploaded{err ? <span className="text-red-400"> · {err} failed</span> : null}</span>
        </div>
        {!allDone ? (
          <button onClick={start} disabled={running || items.length === 0} data-testid="batch-start"
            className="w-full h-14 rounded-2xl bg-teal-500 disabled:bg-white/10 disabled:text-slate-400 text-slate-900 font-black text-lg">
            {running ? `Uploading… ${ok}/${items.length}` : `Upload ${items.length} photo${items.length === 1 ? "" : "s"}`}
          </button>
        ) : (
          <button onClick={() => onDone(ok)} data-testid="batch-done"
            className="w-full h-14 rounded-2xl bg-emerald-600 text-white font-black text-lg">
            {err ? `Continue with ${ok} (retry failed later)` : "Done — continue"}
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid="batch-input"/>
    </div>
  );
}
